import React from 'react';
import { Invoice } from '../../../types/domain';
import { formatBillingDate, formatCurrency } from '../types';
import { HedgehogSpinner } from '../../../components/brand/HedgehogSpinner';
import { DataGridPagination } from '../../../components/grid/DataGridPagination';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import {
  FileText,
  Receipt,
  CheckCircle2,
} from 'lucide-react';

interface InvoiceHistoryTableProps {
  invoices: Invoice[];
  isLoading: boolean;
  totalInvoices: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  selectedOrgFilter: string;
  onOrgFilterChange: (orgId: string) => void;
  organizations: Array<{ id: string; name: string }>;
  onSelectReceipt: (inv: Invoice) => void;
}

export const InvoiceHistoryTable: React.FC<InvoiceHistoryTableProps> = ({
  invoices,
  isLoading,
  totalInvoices,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  selectedOrgFilter,
  onOrgFilterChange,
  organizations,
  onSelectReceipt,
}) => {
  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-xs font-semibold text-cf-textPrimary uppercase tracking-wider font-mono">
              Invoices & Payment History
            </h3>
            <span className="px-1.5 py-0.5 rounded bg-cf-surface border border-cf-border text-cf-textSecondary text-xs font-mono">
              {totalInvoices}
            </span>
          </div>
          <p className="text-xs text-cf-textSecondary mt-0.5">
            Download receipts and audit complete billing transactions across all organizations.
          </p>
        </div>

        {organizations.length > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-cf-textMuted shrink-0">Filter:</span>
            <Select
              size="xs"
              value={selectedOrgFilter}
              onChange={(e) => onOrgFilterChange(e.target.value)}
              className="w-48"
              options={[
                { value: 'all', label: 'All Organizations' },
                ...organizations.map((org) => ({
                  value: org.id,
                  label: org.name,
                })),
              ]}
            />
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="py-12 flex flex-col items-center justify-center gap-2 text-cf-textMuted">
          <HedgehogSpinner size="md" />
          <span className="text-xs">Loading billing records...</span>
        </div>
      ) : totalInvoices === 0 ? (
        <div className="p-8 rounded-lg bg-cf-surface border border-cf-border text-center space-y-2">
          <FileText size={28} className="mx-auto text-cf-textMuted" />
          <h4 className="text-xs font-semibold text-cf-textPrimary">No Invoices Found</h4>
          <p className="text-xs text-cf-textSecondary max-w-sm mx-auto">
            When your organizations subscribe to paid plans or renew billing cycles, receipts will appear here automatically.
          </p>
        </div>
      ) : (
        <div className="rounded-lg bg-cf-surface border border-cf-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-cf-border bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader text-cf-textMuted uppercase font-mono text-xs">
                  <th className="py-2.5 px-4 font-semibold bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader">Invoice #</th>
                  <th className="py-2.5 px-4 font-semibold bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader">Organization</th>
                  <th className="py-2.5 px-4 font-semibold bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader">Plan</th>
                  <th className="py-2.5 px-4 font-semibold bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader">Billing Period</th>
                  <th className="py-2.5 px-4 font-semibold bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader">Amount</th>
                  <th className="py-2.5 px-4 font-semibold bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader">Status</th>
                  <th className="py-2.5 px-4 font-semibold text-right bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cf-border">
                {invoices.map((inv) => (
                  <tr key={inv.id} className="hover:bg-cf-surfaceHover/50 transition-colors">
                    <td className="py-3 px-4 font-mono font-medium text-cf-textPrimary">
                      <div className="flex items-center gap-1.5">
                        <Receipt size={13} className="text-cf-textMuted" />
                        <span>{inv.invoice_number}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-cf-textPrimary font-medium">
                      {inv.organization_name || 'Organization'}
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-1.5 py-0.5 rounded bg-cf-bg border border-cf-border text-cf-textSecondary text-xs font-mono uppercase font-semibold">
                        {inv.plan_tier}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-cf-textSecondary text-xs">
                      {formatBillingDate(inv.billing_period_start)} – {formatBillingDate(inv.billing_period_end)}
                    </td>
                    <td className="py-3 px-4 font-mono font-semibold text-cf-textPrimary">
                      {formatCurrency(inv.amount_cents, inv.currency)}
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 text-xs font-bold font-mono uppercase">
                        <CheckCircle2 size={10} />
                        <span>{inv.status}</span>
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Button
                        type="button"
                        variant="secondary"
                        size="xs"
                        onClick={() => onSelectReceipt(inv)}
                        leftIcon={<FileText size={12} />}
                      >
                        Receipt
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Bar */}
          <DataGridPagination
            page={page}
            pageSize={pageSize}
            totalRows={totalInvoices}
            pageSizeOptions={[10, 25, 50, 100]}
            unitLabel="invoices"
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      )}
    </div>
  );
};
